/** @jsx jsx */
import { Fragment, useState } from 'react'
import { css, jsx, keyframes } from '@emotion/core'
import useMedia from 'use-media'

import RaportManager from './RaportManager'
import {
  SingleChoiceChart,
  MultiChoiceChart,
  TextAnswerChart,
  ScoreChart,
} from './'

const showCharts = keyframes`
  to {
    opacity: 1;
  }
`

const reportCss = css`
  overflow-y: scroll;
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;

  .export {
    margin-bottom: 40px;

    @media (max-width: 640px) {
      margin-bottom: 30px;
    }
  }

  .charts {
    width: calc(100% - 100px);
    display: grid;
    grid-template-columns: 1fr 1fr;
    justify-items: center;
    grid-gap: 50px;
    padding: 0 50px;
    opacity: 0;
    animation: ${showCharts} 0.2s ease forwards 0.3s;

    @media (max-width: 980px) {
      grid-template-columns: 1fr;
    }

    @media (max-width: 550px) {
      padding: 0;
      width: 100%;
    }
  }

  .empty-box {
    height: 270px;
  }
`

const SummaryReport = ({
  surveyName,
  structure,
  response,
  isLoading: isResponseLoading,
  updateFeeback,
  setSelectDisabled,
  surveyId,
  setSurveysLoading,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isDisabled, setIsDisabled] = useState(false)
  const isNarrow = useMedia({ maxWidth: 980 })
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)

  const isPending = isResponseLoading || isLoading

  const refresh = () => {
    setIsLoading(true)
    setIsDisabled(true)
    setSelectDisabled(true)
    setIsTooltipOpen(false)
    setSurveysLoading(true)
    setTimeout(() => {
      updateFeeback().then(() => {
        setIsLoading(false)
        setIsDisabled(false)
        setSelectDisabled(false)
        setIsTooltipOpen(false)
        setSurveysLoading(false)
      })
    }, 3000)
  }

  return (
    <div css={reportCss}>
      <RaportManager
        response={response}
        refresh={refresh}
        setIsTooltipOpen={setIsTooltipOpen}
        isTooltipOpen={isTooltipOpen}
        surveyName={surveyName}
        structure={structure}
        surveysLoading={isDisabled || isResponseLoading}
        isDisabled={isDisabled}
      />

      <div className="charts">
        {structure.map((field, fieldIndex) => {
          if (field.type === 'TEXT_ANSWER') {
            return (
              <TextAnswerChart
                key={`${surveyId}-summary-text-answer-${fieldIndex}`}
                field={field}
                fieldIndex={fieldIndex}
                response={response}
                isLoading={isPending}
                surveyId={surveyId}
              />
            )
          }
          if (field.type === 'SINGLE_CHOICE') {
            return (
              <SingleChoiceChart
                key={`${surveyId}-summary-single-choice-${fieldIndex}`}
                field={field}
                fieldIndex={fieldIndex}
                response={response}
                structure={structure}
                isLoading={isPending}
                surveyId={surveyId}
              />
            )
          }
          if (field.type === 'MULTI_CHOICE') {
            return (
              <MultiChoiceChart
                key={`${surveyId}-summary-multi-choice-${fieldIndex}`}
                field={field}
                fieldIndex={fieldIndex}
                response={response}
                structure={structure}
                isLoading={isPending}
                surveyId={surveyId}
              />
            )
          }
          if (field.type === 'SCORE') {
            return (
              <ScoreChart
                key={`${surveyId}-summary-multi-choice-${fieldIndex}`}
                field={field}
                fieldIndex={fieldIndex}
                response={response}
                structure={structure}
                isLoading={isPending}
                surveyId={surveyId}
              />
            )
          }

          return null
        })}
        {isNarrow ? (
          <div className="empty-box" />
        ) : (
          <Fragment>
            <div className="empty-box" />
            <div className="empty-box" />
          </Fragment>
        )}
      </div>
    </div>
  )
}

export default SummaryReport
